// src/services/address/gps-external/factory.js
// *******************************************************************************
// GPS Provider Factory - Multi-provider with automatic fallback
// *******************************************************************************
// Factory chain: Provider 1 → Provider 2 → ...
// Based on backend pattern: src/modules/integrations/factory-helpers.js
// *******************************************************************************

import jarvislyGpsAdapter from './jarvisly/adapter.js';
import nominatimGpsAdapter from './nominatim/adapter.js';
import positionstackGpsAdapter from './positionstack/adapter.js';
import { createProviderFactory } from '../factory-helpers.js';

/**
 * GPS Provider registry
 * Order matters: First provider = highest priority
 * Default chain: Jarvisly (backend Google Maps) → Nominatim (OSM) → Positionstack
 */
const GPS_REGISTRY = {
  jarvisly: { name: 'jarvisly', adapter: jarvislyGpsAdapter },
  nominatim: { name: 'nominatim', adapter: nominatimGpsAdapter },
  positionstack: { name: 'positionstack', adapter: positionstackGpsAdapter },
};

/**
 * Creates GPS provider instance with automatic fallback
 *
 * @param {Object} [options] - Factory options
 * @param {string|Array<string>} [options.provider] - Provider(s) to use
 *   - If not specified: uses all providers in default order
 *   - If string: single provider or comma-separated ("jarvisly,nominatim")
 *   - If array: ordered provider names (["nominatim", "jarvisly"])
 *
 * @returns {Object} - Provider instance
 * @returns {Array<string>} return.chain - Active provider chain
 * @returns {Object} return.adapter - Composite adapter with fallback logic
 *
 * @example
 * // Use default chain (all providers)
 * const { adapter } = getGpsProvider();
 * const result = await adapter.searchCoordinates({ address, number, city, region });
 *
 * @example
 * // Use only specific provider
 * const { adapter } = getGpsProvider({ provider: 'jarvisly' });
 *
 * @example
 * // Custom fallback order
 * const { adapter } = getGpsProvider({ provider: ['nominatim', 'jarvisly'] });
 */
export const getGpsProvider = createProviderFactory({
  registry: GPS_REGISTRY,
  methods: ['searchCoordinates'],
});

export default {
  getGpsProvider,
};
